import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { ToastrService } from 'ngx-toastr';
import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

interface User {
  id: number;
  name: string;
  image: string;
  phone_without_code: string | null;
}

interface Message {
  id: number;
  chat_id: string;
  user_id: string;
  message_type: string;
  message: string;
  created_at: string;
  sender: User;
}

interface ChatResponse {
  status: boolean;
  users: {
    user_one: User;
    user_two: User;
  };
  messages: Message[];
}

@Component({
  selector: 'app-chat-details',
  templateUrl: './chat-details.component.html',
  styleUrls: ['./chat-details.component.scss']
})
export class ChatDetailsComponent implements OnInit, OnDestroy {
  currentLang: string;
  chatId: any;
  chatDetailes: any;
  @ViewChild('chatMessages') private chatMessagesContainer!: ElementRef;

  chatData: ChatResponse | null = null;
  currentUserId: number = 41;
  loading: boolean = true;
  error: string = '';
  routeSub: Subscription;
  langChangeSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApisService,
    private toastr: ToastrService,
    private http: HttpClient,
    private translate: TranslateService
  ) {
    this.currentLang = this.translate.currentLang || 'ar';
  }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'ar';
    
    // الاشتراك في تغييرات اللغة
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
    });

    this.chatId = this.route.snapshot.paramMap.get('id');
    if (this.chatId) {
      this.loadchatDeatiles(this.chatId);
    }
  }

  loadchatDeatiles(id: string): void {
    this.loading = true;

    this.apiService.getchatDataById(id).subscribe({
      next: (res: any) => {
        this.chatData = res || this.chatData;
        this.loading = false;
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (err) => {
        console.error('Failed to load chatDetailes:', err);
        this.toastr.error('Failed to load chat details');
        this.loading = false;
      }
    });
  }

  isCurrentUser(message: Message): boolean {
    return message.sender.id === this.currentUserId;
  }

  getOtherUser(): User {
    if (!this.chatData) return {} as User;
    
    return this.currentUserId === this.chatData.users.user_one.id 
      ? this.chatData.users.user_two 
      : this.chatData.users.user_one;
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    
    if (this.currentLang === 'ar') {
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // التحقق إذا كان التاريخ اليوم أو أمس
    if (date.toDateString() === today.toDateString()) {
      return this.translate.instant('DATE.TODAY');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('DATE.YESTERDAY');
    }

    if (this.currentLang === 'ar') {
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  getMessageContent(message: Message): string {
    if (message.message_type === 'image') {
      return this.translate.instant('MENU.CHAT.IMAGE');
    }
    return message.message;
  }

  isImageMessage(message: Message): boolean {
    return message.message_type === 'image';
  }

  getImageUrl(message: Message): string {
    return `https://miracleih.com.sa/public/storage/${message.message}`;
  }

  openImage(imageUrl: string): void {
    window.open(imageUrl, '_blank');
  }

  scrollToBottom(): void {
    try {
      this.chatMessagesContainer.nativeElement.scrollTop = 
        this.chatMessagesContainer.nativeElement.scrollHeight;
    } catch(err) {
      console.error('Scroll error:', err);
    }
  }

  getMessagesCountText(): string {
    const count = this.chatData?.messages.length || 0;
    if (count === 1) {
      return `1 ${this.translate.instant('MENU.CHAT.MESSAGES')}`;
    } else {
      return this.translate.instant('MENU.CHAT.MESSAGES_PLURAL', { count });
    }
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }
}